import {
  Button,
  OutlinedSelect,
  SelectOptionType,
  useMobile,
} from '@octano/global-ui';
import { useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';

import { Debt, DebtFiltersState, EMPTY_DEBT_FILTERS } from '../types';
import { useDebtLoader } from './DebtLoader';

/** Subset of Period fields needed by the filter. The list endpoint returns
 *  these even though the PeriodItem type used by getPeriodsRequest is narrow. */
export interface PeriodFilterItem {
  id: number;
  code: string;
  name: string;
  academicYear: number;
}

const keyPrefix = 'financialPortal.currentAccount.tabs.currentAccount.filters';

// HU #15319 — Filter bar rendered between DebtHeader and the action row.
//
// Per XD design: AÑO, PERIODO, PLAN DE ESTUDIO (no ESTADO selector here;
// ESTADO is shown as a chip badge in the action row below).

interface FormShape {
  year: SelectOptionType | null;
  period: SelectOptionType | null;
  studyPlanId: SelectOptionType | null;
}

const EMPTY_FORM: FormShape = {
  year: null,
  period: null,
  studyPlanId: null,
};

function uniqueBy<T, K>(items: T[], keyFn: (item: T) => K): T[] {
  const seen = new Set<K>();
  const out: T[] = [];
  for (const item of items) {
    const key = keyFn(item);
    if (!seen.has(key)) {
      seen.add(key);
      out.push(item);
    }
  }
  return out;
}

interface DebtFiltersProps {
  periods: PeriodFilterItem[];
}

export default function DebtFilters({ periods }: DebtFiltersProps) {
  const { t } = useTranslation();
  const isMobile = useMobile();
  const { data, updateQuery } = useDebtLoader();

  const { control, handleSubmit, reset } = useForm<FormShape>({
    defaultValues: EMPTY_FORM,
  });

  const selectedYear = useWatch({ control, name: 'year' });

  const yearOptions = useMemo<SelectOptionType[]>(() => {
    const years = Array.from(new Set(periods.map((p) => p.academicYear))).sort(
      (a, b) => b - a,
    );
    return years.map((y) => ({ label: String(y), value: y }));
  }, [periods]);

  const periodOptions = useMemo<SelectOptionType[]>(() => {
    const filtered =
      selectedYear?.value != null
        ? periods.filter(
            (p) => p.academicYear === (selectedYear.value as number),
          )
        : periods;
    return uniqueBy(
      filtered.map((p) => ({
        label: p.name,
        value: parseInt(p.code.slice(-1), 10),
      })),
      (o) => o.value,
    );
  }, [periods, selectedYear]);

  const debts: Debt[] = data?.debts ?? [];

  const studyPlanOptions = useMemo<SelectOptionType[]>(() => {
    const plans = debts
      .filter(
        (d): d is Debt & { studyPlanId: number; studyPlanName: string } =>
          d.studyPlanId !== null && d.studyPlanName !== null,
      )
      .map((d) => ({ id: d.studyPlanId, name: d.studyPlanName }));
    return uniqueBy(plans, (p) => p.id).map((p) => ({
      label: p.name,
      value: p.id,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.debts]);

  const onSubmit = (values: FormShape) => {
    const next: DebtFiltersState = {
      year: (values.year?.value as number | undefined) ?? null,
      period: (values.period?.value as number | undefined) ?? null,
      studyPlanId: (values.studyPlanId?.value as number | undefined) ?? null,
      status: null,
    };
    updateQuery(next);
  };

  const onClear = () => {
    reset(EMPTY_FORM);
    updateQuery(EMPTY_DEBT_FILTERS);
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)} className="pt-3 pb-5">
      <Row className="gutters-filters">
        <Col xs={12} md={3}>
          <OutlinedSelect
            name="year"
            control={control}
            label={t(`${keyPrefix}.year`)}
            placeholder={t(`${keyPrefix}.all`)}
            options={yearOptions}
            isClearable
          />
        </Col>
        <Col xs={12} md={3}>
          <OutlinedSelect
            name="period"
            control={control}
            label={t(`${keyPrefix}.period`)}
            placeholder={t(`${keyPrefix}.all`)}
            options={periodOptions}
            isClearable
          />
        </Col>
        <Col xs={12} md={3}>
          <OutlinedSelect
            name="studyPlanId"
            control={control}
            label={t(`${keyPrefix}.studyPlan`)}
            placeholder={t(`${keyPrefix}.all`)}
            options={studyPlanOptions}
            isClearable
          />
        </Col>
        <Col xs={12} md={3}>
          <div className="d-flex justify-content-end">
            <Button
              type="submit"
              text={t(`${keyPrefix}.search`)}
              size="sm"
              style={{
                width: '100%',
                height: 41,
                marginTop: isMobile ? 0 : '1.5rem',
              }}
            />
            <Button
              text={t(`${keyPrefix}.clear`)}
              size="sm"
              outlined
              onClick={onClear}
              style={{
                width: '100%',
                height: 41,
                marginTop: isMobile ? '1rem' : '1.5rem',
                marginLeft: 10,
              }}
            />
          </div>
        </Col>
      </Row>
    </Form>
  );
}
